import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    loadBenefitCodeDetails,
    loadJobCentreDetails,
    loadReadinessToChangeDetails,
    loadRightToWorkDocTypes,
    loadTimeUnemployedDetails
} from '../../../store/directusService';
import {
    clearParticipantEmployability,
    loadParticipantEmployability,
    updateParticipantEmployability
} from '../../../store/participantService';
import { getConfiguredItems, getObjFromId } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    BENEFIT_CODE_DETAILS_ERROR,
    JOB_CENTRE_DETAILS_ERROR,
    READINESS_TO_CHANGE_ERROR,
    RIGHT_TO_WORK_DOCTYPES_ERROR,
    TIME_UNEMPLOYED_DETAILS_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import LoadingSpinner from '../../ui/LoadingSpinner';
import JobGoals from '../jobGoals/JobGoals';

import JobReadinessManagement from './jobReadiness/JobReadinessManagement';
import OccManagement from './occ/OccManagement';
import { validate } from './validateEmployability';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './employability.module.css';

const JOB_CENTRE_PLUS_NAME_LENGTH = 100;
const JOB_CENTRE_PLUS_EMAIL_LENGTH = 254;
const message = 'Participant employability details have been updated';
const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const Employability = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: null,
        participantId: '',
        allowMandatoryActivities: false,
        benefitCodeId: '',
        jobCentreId: '',
        jobCentrePlusContactEmail: '',
        jobCentrePlusContactName: '',
        jobGoals: [],
        jobReadinessId: '',
        readinessToChangeId: '',
        rightToWorkDocumentExpiryDate: '',
        rightToWorkDocumentIdNumber: '',
        rightToWorkDocumentTypeId: '',
        timeUnemployedId: '',
        entitledToPublicFunds: false
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [type, setType] = useState('create');

    const [arrayJobCentres, setArrayJobCentres] = useState([]);
    const [arrayRightToWorkDocumentTypes, setArrayRightToWorkDocumentTypes] = useState([]);
    const [arrayReadinessToChange, setArrayReadinessToChange] = useState([]);
    const [arrayTimeUnemployed, setArrayTimeUnemployed] = useState([]);
    const [arrayBenefitCodes, setArrayBenefitCodes] = useState([]);

    const [rightToWorkDocumentType, setRightToWorkDocumentType] = useState({});
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantEmployability = useSelector(
        (state) => state.entities.participantService.currentParticipantEmployability
    );
    const jobCentreDetails = useSelector(
        (state) => state.entities.directusService.jobCentreDetails
    );
    const readinessToChangeDetails = useSelector(
        (state) => state.entities.directusService.readinessToChangeDetails
    );
    const timeUnemployedDetails = useSelector(
        (state) => state.entities.directusService.timeUnemployedDetails
    );
    const benefitCodeDetails = useSelector(
        (state) => state.entities.directusService.benefitCodeDetails
    );
    const rightToWorkDocTypes = useSelector(
        (state) => state.entities.directusService.rightToWorkDocTypes
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const errorMessage = useSelector((state) => state.entities.formsState.errorMessage);

    // USE EFFECTS
    useEffect(() => {
        readinessToChangeDetails?.length < 1 && dispatch(loadReadinessToChangeDetails());
        jobCentreDetails?.length < 1 && dispatch(loadJobCentreDetails());
        timeUnemployedDetails?.length < 1 && dispatch(loadTimeUnemployedDetails());
        benefitCodeDetails?.length < 1 && dispatch(loadBenefitCodeDetails());
        rightToWorkDocTypes?.length < 1 && dispatch(loadRightToWorkDocTypes());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if ('id' in currentParticipant) {
            dispatch(loadParticipantEmployability(currentParticipant.id));
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (!currentParticipant?.id) return;
        const isDisabledOnClosedStatus =
            currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
        setIsDisabled(!hasRole(acceptedRoles, roles) || isDisabledOnClosedStatus);
    }, [currentParticipant.id]);

    useEffect(() => {
        if (
            Object.keys(participantEmployability).length < 1 ||
            participantEmployability?.participantId !== currentParticipant?.id
        ) {
            setNewEntry({ ...initialState, participantId: currentParticipant?.id });
            setType('create');
        } else {
            setNewEntry(participantEmployability);
            setInitialStateOnEntry(participantEmployability);
            setType('edit');
        }

        if (participantEmployability.rightToWorkDocumentTypeId)
            updateRightToWorkDocumentType(participantEmployability.rightToWorkDocumentTypeId);
    }, [participantEmployability]);

    useEffect(() => {
        if (readinessToChangeDetails?.length) {
            setArrayReadinessToChange(
                getConfiguredItems(readinessToChangeDetails, currentParticipant.contractId)
            );
        }
    }, [readinessToChangeDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (jobCentreDetails?.length) {
            setArrayJobCentres(
                getConfiguredItems(jobCentreDetails, currentParticipant?.contractId)
            );
        }
    }, [jobCentreDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (timeUnemployedDetails?.length) {
            setArrayTimeUnemployed(
                getConfiguredItems(timeUnemployedDetails, currentParticipant?.contractId)
            );
        }
    }, [timeUnemployedDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (benefitCodeDetails?.length) {
            setArrayBenefitCodes(
                getConfiguredItems(benefitCodeDetails, currentParticipant?.contractId)
            );
        }
    }, [benefitCodeDetails, currentParticipant?.contractId]);

    useEffect(() => {
        if (rightToWorkDocTypes?.length) {
            setArrayRightToWorkDocumentTypes(
                getConfiguredItems(rightToWorkDocTypes, currentParticipant?.contractId)
            );
        }
    }, [rightToWorkDocTypes, currentParticipant?.contractId]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === message || errorMessage) {
            setIsUpdateButtonDisabled(false);
        }
    }, [successMessage, errorMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (readinessToChangeDetails?.length < 1) return READINESS_TO_CHANGE_ERROR;
        if (jobCentreDetails?.length < 1) return JOB_CENTRE_DETAILS_ERROR;
        if (timeUnemployedDetails?.length < 1) return TIME_UNEMPLOYED_DETAILS_ERROR;
        if (benefitCodeDetails?.length < 1) return BENEFIT_CODE_DETAILS_ERROR;
        if (rightToWorkDocTypes?.length < 1) return RIGHT_TO_WORK_DOCTYPES_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const updateRightToWorkDocumentType = (id) => {
        const doc = arrayRightToWorkDocumentTypes.find((el) => el.id === id);
        setRightToWorkDocumentType(doc || {});
        if (doc && !doc.expiry_date_required && newEntry.rightToWorkDocumentExpiryDate)
            setNewEntry((prev) => ({ ...prev, rightToWorkDocumentExpiryDate: '' }));
    };

    const clearError = (key) => {
        if (key in errors) setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const cleanUp = () => dispatch(clearParticipantEmployability());

    // EVENT HANDLERS
    const updateJobGoals = (jobGoals) => {
        setNewEntry((prev) => ({ ...prev, jobGoals }));
    };

    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onUpdateRightToWorkDocumentType = (id) => {
        updateRightToWorkDocumentType(id);
        onUpdate('rightToWorkDocumentTypeId', id);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry, rightToWorkDocumentType, type);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            jobGoals: newEntry.jobGoals.map((el) => ({
                ...el,
                id: el.id.startsWith('new-') ? null : el.id
            }))
        };
        dispatch(updateParticipantEmployability(payload, message));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={local.employabilityWrapper}>
            <form
                onSubmit={onSubmit}
                data-testid="form_start_edit_employability"
                className={`${form.form} ${local.formContainer}`}>
                <h2 className={app.sectionHeading}>General Details</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="jobCentreId"
                            label="Job Centre"
                            placeholder="Select a job centre"
                            disabled={isDisabled}
                            menuItems={arrayJobCentres || []}
                            selectedId={newEntry.jobCentreId || ''}
                            selected={getObjFromId(arrayJobCentres, newEntry.jobCentreId)}
                            onChange={(id) => onUpdate('jobCentreId', id)}
                        />
                        <TextInputField
                            id="jobCentrePlusContactName"
                            label={'Job Centre Plus Contact Name'}
                            value={newEntry.jobCentrePlusContactName || ''}
                            placeholder={'Enter job centre plus contact name'}
                            disabled={isDisabled}
                            maxLength={JOB_CENTRE_PLUS_NAME_LENGTH}
                            error={errors.jobCentrePlusContactName}
                            onChange={(e) => onUpdate('jobCentrePlusContactName', e.target.value)}
                        />

                        <TextInputField
                            id="jobCentrePlusContactEmail"
                            label="Job Centre Plus Contact Email"
                            value={newEntry.jobCentrePlusContactEmail || ''}
                            placeholder={'Enter job centre plus contact email'}
                            disabled={isDisabled}
                            maxLength={JOB_CENTRE_PLUS_EMAIL_LENGTH}
                            error={errors.jobCentrePlusContactEmail}
                            onChange={(e) => onUpdate('jobCentrePlusContactEmail', e.target.value)}
                        />
                        <RadioButtons
                            id="allowMandatoryActivities"
                            label={'Allow Mandatory Activities?'}
                            disabled={!hasRole([QUALITY, SUPERUSER], roles)}
                            value={newEntry.allowMandatoryActivities || false}
                            onChange={(option) => onUpdate('allowMandatoryActivities', option)}
                        />
                        <SingleSelect
                            id="rightToWorkDocumentTypeId"
                            label="Right To Work Document Type Id"
                            placeholder="Select a right to work document type"
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={arrayRightToWorkDocumentTypes || []}
                            selectedId={newEntry.rightToWorkDocumentTypeId || ''}
                            selected={getObjFromId(
                                arrayRightToWorkDocumentTypes,
                                newEntry.rightToWorkDocumentTypeId
                            )}
                            error={errors?.rightToWorkDocumentTypeId}
                            onChange={(id) => onUpdateRightToWorkDocumentType(id)}
                        />
                        {rightToWorkDocumentType?.expiry_date_required && (
                            <DateSelect
                                id="rightToWorkDocumentExpiryDate"
                                label="Expiry Date"
                                disabled={isDisabled}
                                mandatory={true}
                                value={newEntry.rightToWorkDocumentExpiryDate}
                                isDefault={false}
                                error={errors.rightToWorkDocumentExpiryDate}
                                onDateChange={(date) =>
                                    onUpdate('rightToWorkDocumentExpiryDate', date)
                                }
                            />
                        )}
                        <TextInputField
                            id="rightToWorkDocumentIdNumber"
                            label="Right to Work ID Number"
                            placeholder="Enter the right to work ID number"
                            disabled={isDisabled}
                            value={newEntry.rightToWorkDocumentIdNumber || ''}
                            onChange={(e) =>
                                onUpdate('rightToWorkDocumentIdNumber', e.target.value)
                            }
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="timeUnemployedId"
                            label="Time Unemployed"
                            placeholder="Select time unemployed"
                            disabled={isDisabled}
                            menuItems={arrayTimeUnemployed || []}
                            selectedId={newEntry.timeUnemployedId || ''}
                            selected={getObjFromId(arrayTimeUnemployed, newEntry.timeUnemployedId)}
                            onChange={(id) => onUpdate('timeUnemployedId', id)}
                        />

                        <SingleSelect
                            id="benefitCodeId"
                            label="Benefit Code"
                            placeholder="Select benefit code"
                            disabled={isDisabled}
                            menuItems={arrayBenefitCodes || []}
                            selectedId={newEntry.benefitCodeId || ''}
                            selected={getObjFromId(arrayBenefitCodes, newEntry.benefitCodeId)}
                            onChange={(id) => onUpdate('benefitCodeId', id)}
                        />
                        <SingleSelect
                            id="readinessToChangeId"
                            label="Readiness to Change"
                            placeholder="Select readiness to change"
                            disabled={isDisabled}
                            menuItems={arrayReadinessToChange || []}
                            selectedId={newEntry.readinessToChangeId || ''}
                            selected={getObjFromId(
                                arrayReadinessToChange,
                                newEntry.readinessToChangeId
                            )}
                            onChange={(id) => onUpdate('readinessToChangeId', id)}
                        />
                        <RadioButtons
                            id="entitledToPublicFunds"
                            label={'Are you entitled to public funds?'}
                            disabled={isDisabled}
                            value={newEntry.entitledToPublicFunds || false}
                            onChange={(option) => onUpdate('entitledToPublicFunds', option)}
                        />
                    </div>
                </div>

                <JobGoals
                    currentParticipant={currentParticipant}
                    isDisabledFromParent={isDisabled}
                    employabilityJobGoals={newEntry?.jobGoals || []}
                    updateGoalsForEmployability={updateJobGoals}
                    isEmployability={true}
                />
                <Button
                    id="employabilityUpdateButton"
                    disabled={isDisabled || isUpdateButtonDisabled}
                    content="Update Employability"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>

            <JobReadinessManagement />
            <OccManagement currentParticipant={currentParticipant} />
        </div>
    );
};

export default Employability;
